/**
 * Normally, the OIDC client should handle silent renew automatically.
 * However, in our case, the Salesforce Identity Provider does not provide a valid `expires_in`
 * value in the token response, which prevents the OIDC client from scheduling the silent renew.
 */

import { saveRedirectAfterSignin } from "@src/core/auth/callbacks";
import { signinSilentOnce } from "@src/core/auth/signinSilentOnce";
import { jwtDecode } from "jwt-decode";
import { User } from "oidc-client-ts";
import { PropsWithChildren, use, useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { FeatureFlagsContext } from "./FeatureFlags/FeatureFlagsContext";

type SalesforceSilentRenewProps = PropsWithChildren<{
  onLoggedIn?: (user: User) => void;

  // Time in seconds before token expiration to trigger silent renew. Default is 1 minute.
  renewalBuffer?: number;

  // Time in seconds to wait before retrying silent renew after a failure. Default is 30 seconds.
  retryDelay?: number;
}>;

export function SalesforceSilentRenew({
  children,
  onLoggedIn,
  renewalBuffer = 60 * 5,
  retryDelay = 30,
}: SalesforceSilentRenewProps) {
  const { isFeatureEnabled } = use(FeatureFlagsContext);
  const isAuthEnabled = isFeatureEnabled("authentication");
  const auth = useAuth();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isAuthEnabled || !auth.user?.access_token) return;
    const decoded = jwtDecode(auth.user.access_token);
    if (!decoded.exp) return;

    function clearTimers() {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    }

    async function handleSilentRenew(timeToExpire: number) {
      const user = await signinSilentOnce(auth.signinSilent);
      if (!user) {
        if (timeToExpire <= 0) {
          // If the token is already expired, sign out the user
          saveRedirectAfterSignin();
          window.location.href = "/auth/logout";
          return;
        }
        // If silent renew fails, retry after the specified delay
        retryTimerRef.current = setTimeout(() => {
          handleSilentRenew(timeToExpire - retryDelay * 1000);
        }, retryDelay * 1000);
      } else {
        onLoggedIn?.(user);
      }
    }

    const expMs = decoded.exp * 1000;

    // Timers are throttled or paused while the tab is hidden or the device sleeps,
    // so check the token again as soon as the user comes back.
    function handleVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      const timeToExpire = expMs - Date.now();
      if (timeToExpire > renewalBuffer * 1000) return;
      clearTimers();
      handleSilentRenew(timeToExpire);
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const now = Date.now();
    const timeToExpire = expMs - now;
    const delay = timeToExpire - renewalBuffer * 1000;
    if (delay <= 0) {
      handleSilentRenew(timeToExpire);
    } else {
      timerRef.current = setTimeout(() => {
        handleSilentRenew(timeToExpire);
      }, delay);
      onLoggedIn?.(auth.user);
    }

    return () => {
      clearTimers();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.access_token, isAuthEnabled]);

  /**
   * When requesting a new token, Salesforce adds a scope in the response. The oidc client
   * then tries to re-use the scope when refreshing the token, but Salesforce does not accept
   * the scope parameter when refreshing a token, which results in a bad request.
   * To fix this, we remove the scope from the user profile after loading it.
   */
  useEffect(() => {
    const removeScope = (user: User) => {
      if (!user?.scope) return;
      delete user.scope;
      auth.settings.userStore?.set(
        `user:${auth.settings.authority}:${auth.settings.client_id}`,
        JSON.stringify(user),
      );
    };

    auth.events.addUserLoaded(removeScope);

    return () => {
      auth.events.removeUserLoaded(removeScope);
    };
  }, [auth]);

  return children;
}
